/**
 * VATP013 disbursement evidence for a pass-through the typing centre paid (government fee, no-VAT add-on, direct
 * invoice fee): classification (Agent), how the customer authorised it, an optional receipt number and the receipt
 * file. The file is the evidence — authorisation + file is accepted (verbal waits for finance review); without the
 * file the item shows "Evidence needed". The evidence is optional: OptionalDisbursement keeps it behind
 * "+ Add disbursement" until someone adds it, and only started evidence is checked by the gates.
 *
 * The picked file is kept in `draft.file` and uploaded by the caller's Save, so a new item can get its id first.
 */

import React, { useRef, useState } from 'react';
import {
  Box, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button, Typography, Alert, CircularProgress,
} from '@mui/material';
import { AttachFile, Visibility, UploadFile, Add, Close } from '@mui/icons-material';
import StatusChip from './StatusChip';
import { EVIDENCE } from './PaymentSourceFields';
import { serverMessage } from '../../utils/apiConvert';

const MAX_BYTES = 10 * 1024 * 1024;

const DisbursementEvidenceFields = ({ draft, set, status, hasReceipt, fetchUrl, disabled, dense = false, onError }) => {
  const input = useRef(null);
  const [opening, setOpening] = useState(false);
  const font = dense ? { fontSize: '0.75rem' } : {};
  const inputSx = dense ? { '& .MuiInputBase-input': font, '& .MuiInputLabel-root': font } : {};
  const ev = EVIDENCE[status];

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!(file.type.startsWith('image/') || file.type === 'application/pdf')) {
      onError?.('The receipt must be an image or a PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.('The receipt file exceeds 10 MB');
      return;
    }
    set({ file });
  };

  const view = async () => {
    setOpening(true);
    try {
      const r = await fetchUrl();
      if (r?.downloadUrl) window.open(r.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      onError?.(serverMessage(err, 'Could not open the receipt'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" disabled>
            <InputLabel sx={font}>Classification</InputLabel>
            <Select value="AGENT" label="Classification" sx={font}>
              <MenuItem value="AGENT" sx={font}>Agent (No VAT on fee)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel sx={font}>How did the customer authorise it?</InputLabel>
            <Select value={draft.authType || ''} label="How did the customer authorise it?" sx={font}
              onChange={(e) => set({ authType: e.target.value })}>
              <MenuItem value="" sx={font}><em>—</em></MenuItem>
              <MenuItem value="SIGNED_SERVICE_ORDER" sx={font}>Signed service order</MenuItem>
              <MenuItem value="CUSTOMER_APPROVAL" sx={font}>Customer approval (written)</MenuItem>
              <MenuItem value="VERBAL" sx={font}>Verbal</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Receipt no. (optional)" value={draft.receiptRef || ''}
            disabled={disabled} onChange={(e) => set({ receiptRef: e.target.value })} sx={inputSx} />
        </Grid>
      </Grid>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={1}>
        <AttachFile sx={{ fontSize: 16 }} color={hasReceipt || draft.file ? 'primary' : 'disabled'} />
        <Typography sx={{ fontSize: dense ? '0.7rem' : '0.85rem' }} color={hasReceipt || draft.file ? 'text.primary' : 'text.secondary'}>
          {draft.file ? `${draft.file.name} — uploads on save` : hasReceipt ? 'Receipt attached' : 'No receipt file (in the customer’s name)'}
        </Typography>
        {opening && <CircularProgress size={14} />}
        {hasReceipt && fetchUrl && (
          <Button size="small" startIcon={<Visibility sx={{ fontSize: 14 }} />} onClick={view} disabled={opening}
            sx={{ textTransform: 'none', ...font }}>View</Button>
        )}
        {!disabled && (
          <Button size="small" startIcon={<UploadFile sx={{ fontSize: 14 }} />} onClick={() => input.current?.click()}
            sx={{ textTransform: 'none', ...font }}>
            {hasReceipt || draft.file ? 'Replace' : 'Attach receipt'}
          </Button>
        )}
        <input ref={input} type="file" accept="image/*,application/pdf" hidden onChange={pick} />
      </Box>

      <Box sx={{ mt: 1 }}>
        {ev ? <StatusChip tone={ev.tone} label={ev.label} /> : <StatusChip tone="error" label="Evidence needed" />}
      </Box>
      {draft.authType === 'VERBAL' && (
        <Alert severity="warning" sx={{ mt: 1, py: 0, ...(dense ? { fontSize: '0.7rem' } : {}) }}>
          Verbal authorisation goes to the accountant for review.
        </Alert>
      )}
    </Box>
  );
};

/** True when the draft has something to send: a new file, or an authorisation / receipt number that changed. */
export const evidenceChanged = (draft, saved) => Boolean(draft) && (Boolean(draft.file)
  || (Boolean(draft.authType) && (draft.authType !== (saved?.authType || '')
    || (draft.receiptRef || '').trim() !== (saved?.receiptRef || ''))));

/**
 * Optional evidence: a "+ Add disbursement" button until someone adds it (or any of it was saved), then the fields
 * (children) and "Remove disbursement" - onRemove clears saved evidence, onDiscard drops an unsaved draft.
 */
export const OptionalDisbursement = ({ saved, onRemove, onDiscard, disabled, dense = false, children }) => {
  const [adding, setAdding] = useState(false);
  const font = dense ? { fontSize: '0.75rem' } : {};
  const isSaved = Boolean(saved?.authType || saved?.receiptRef || saved?.hasReceipt);
  if (!isSaved && !adding) {
    return disabled ? null : (
      <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => setAdding(true)}
        sx={{ textTransform: 'none', ...font }}>Add disbursement</Button>
    );
  }
  const remove = async () => {
    if (isSaved) await onRemove();
    else onDiscard?.();
    setAdding(false);
  };
  return (
    <Box>
      {children}
      {!disabled && (
        <Button size="small" color="error" startIcon={<Close sx={{ fontSize: 14 }} />} onClick={remove}
          sx={{ textTransform: 'none', mt: 0.5, ...font }}>Remove disbursement</Button>
      )}
    </Box>
  );
};

export default DisbursementEvidenceFields;
